import axios from "axios";
import { useCallback, useMemo } from "react";

// Dynamische Base URL für Production und Development
const getBaseURL = () => {
  if (typeof window !== "undefined") {
    // Production: gleiche Origin wie Frontend
    return window.location.origin;
  }
  // Development/SSR fallback
  return "";
};

// Einfacher API-Client für das Multi-View-System
const createApiClient = () => {
  const client = axios.create({
    baseURL: getBaseURL(),
    headers: {
      "Content-Type": "application/json",
    },
    timeout: 10000, // 10 Sekunden Timeout
  });

  // Add request interceptor to include auth token if available
  client.interceptors.request.use(
    (config) => {
      // Get token from localStorage if available
      const token = localStorage.getItem("customer_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  return client;
};

export const useApi = () => {
  // Stabile API-Client-Instanz
  const apiClient = useMemo(() => createApiClient(), []);

  // Memoized API-Funktionen um useEffect Dependencies zu stabilisieren
  const get = useCallback(
    async (url: string, config?: any) => {
      // Debug für View-Data-Requests (Line Break Diagnose)
      if (url.includes("/api/views/") && url.includes("/data")) {
        console.log("🌐 API REQUEST DEBUG:", { url, baseURL: getBaseURL() });

        try {
          // Get token for debug request
          const token = localStorage.getItem("customer_token");
          const headers: any = { "Content-Type": "application/json" };
          if (token) {
            headers["Authorization"] = `Bearer ${token}`;
          }

          // Hole rohe Response für Debug-Analyse
          const rawResponse = await fetch(`${getBaseURL()}${url}`, {
            headers,
            signal: config?.signal,
          });

          const rawText = await rawResponse.text();

          console.log("🌐 RAW API RESPONSE:", {
            url: url,
            status: rawResponse.status,
            headers: Object.fromEntries(rawResponse.headers.entries()),
            rawTextLength: rawText.length,
            rawTextSample: rawText.substring(0, 500),
            // Suche nach \r\n im rohen Response
            hasRawCRLF: rawText.includes("\\r\\n"),
            hasRawLF: rawText.includes("\\n"),
            crlfCount: (rawText.match(/\\r\\n/g) || []).length,
            lfCount: (rawText.match(/\\n/g) || []).length,
          });

          // Parse JSON und analysiere Unterschiede
          const jsonData = JSON.parse(rawText);

          // Analysiere info_k_s Felder speziell
          if (jsonData.data && Array.isArray(jsonData.data)) {
            const infoKsFields = jsonData.data
              .map((item: any) => item.info_k_s)
              .filter((field: any) => field && typeof field === "string")
              .slice(0, 3); // Erste 3 für Debug

            console.log("🔬 INFO_K_S FIELDS ANALYSIS:", {
              totalInfoKsFields: jsonData.data.length,
              sampleFields: infoKsFields.map((field: string) => ({
                value: field,
                length: field.length,
                hasRawCRLF: field.includes("\r\n"),
                hasEscapedCRLF: field.includes("\\r\\n"),
                charCodes: [...field].slice(0, 20).map((c) => c.charCodeAt(0)),
              })),
            });
          }

          // Verwende axios-response format für Kompatibilität
          return {
            data: jsonData,
            status: rawResponse.status,
            statusText: rawResponse.statusText,
            headers: rawResponse.headers,
          };
        } catch (debugError) {
          console.error("🌐 DEBUG ERROR - Falling back to axios:", debugError);
          // Fallback zu normaler axios-request
        }
      }

      // Merge config with headers if provided
      const finalConfig = config || {};

      const response = await apiClient.get(url, finalConfig);
      return response;
    },
    [apiClient],
  );

  const post = useCallback(
    async (url: string, data?: any, config?: any) => {
      const response = await apiClient.post(url, data, config);
      return response;
    },
    [apiClient],
  );

  const put = useCallback(
    async (url: string, data?: any, config?: any) => {
      const response = await apiClient.put(url, data, config);
      return response;
    },
    [apiClient],
  );

  const deleteMethod = useCallback(
    async (url: string, config?: any) => {
      const response = await apiClient.delete(url, config);
      return response;
    },
    [apiClient],
  );

  return useMemo(
    () => ({
      get,
      post,
      put,
      delete: deleteMethod,
    }),
    [get, post, put, deleteMethod],
  );
};
